import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdPayments, MdArrowBack } from 'react-icons/md';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import { servicesService, paymentService, walletService } from '../services';
import api from '../services/api';
import type {
  Service,
  ServiceCategory,
  PaymentServiceType,
  WalletCard,
  FavoriteService,
  SavedReference,
  RecentPayment,
  QRPaymentData,
} from '../types';
import { Card, LoaderComponent, EmptyState } from '../components/ui';
import {
  PaymentSearchBar,
  FavoriteServices,
  RecentPaymentsSection,
  SavedReferencesSection,
  QRCodeScanner,
  BillEstimateCard,
} from '../components';
import {
  PaymentFormModal,
  PaymentIframeModal,
  CategoryItem,
  ServiceItem,
  getServiceId,
  getServiceName,
  getServiceLogo,
  getServiceMimeType,
  getServiceCode,
  getServicePaymentPageId,
  getCategoryId,
} from '../components/payments';
import AdCards from '../components/payments/AdCards';
import OtherEntityModal from '../components/payments/OtherEntityModal';

// Main Payments Page
const Payments: React.FC = () => {
  const { t } = useTranslation();
  const { categoriaId, categoriaName } = useParams<{
    categoriaId?: string;
    categoriaName?: string;
  }>();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoriaId || null);
  const location = useLocation();
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // A categoria vem do endereço só ao abrir; escolher uma categoria muda o estado mas
  // não o endereço. Um link para /payments ou /payments/<id> (ex.: os cartões de
  // publicidade no fundo da página) reaproveitava a página e não mudava nada —
  // o clique contava, a navegação não acontecia. location.key muda a cada navegação,
  // mesmo para o mesmo endereço.
  useEffect(() => {
    setSelectedCategory(categoriaId || null);
    setSelectedService(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaId, location.key]);
  const [walletCards, setWalletCards] = useState<WalletCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New feature states
  const [favorites, setFavorites] = useState<FavoriteService[]>([]);
  const [savedReferences, setSavedReferences] = useState<SavedReference[]>([]);
  const [recentPayments, setRecentPayments] = useState<RecentPayment[]>([]);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [prefillData, setPrefillData] = useState<{
    referencia?: string;
    entidade?: string;
    montante?: string;
  } | null>(null);

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [otherEntityModalOpen, setOtherEntityModalOpen] = useState(false);
  const [otherEntityLoading, setOtherEntityLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');

  // Determine view mode
  const showCategoriesView = !selectedCategory;
  const showServicesView = selectedCategory && !selectedService;

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Pagamentos');
    loadCategories();
    loadWalletCards();
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setFavorites(paymentService.getFavorites());
    paymentService.loadSavedReferences().then(refs => setSavedReferences(refs));

    // Show localStorage data immediately (instant, no loading state)
    setRecentPayments(paymentService.getRecentPayments(5));

    // Then refresh from API in the background
    const mapPayment = (t: any): RecentPayment => ({
      id: t.id,
      serviceId: t.entidadeId || '',
      serviceName: t.entidade || t.servico || 'Pagamento',
      serviceLogo: t.entidadeLogo || undefined,
      serviceMimeType: t.entidadeMime || undefined,
      referencia: t.referencia || '',
      referenciaServico: t.referenciaServico || undefined,
      entidade: t.entidade || '',
      entidadeSisp: t.entidadeSisp ? String(t.entidadeSisp) : undefined,
      montante: t.valor || 0,
      // O tipo verdadeiro do pagamento. Antes ia sempre 'service', que a API não
      // conhecia e cobrava como compra sem pagar a factura.
      tipoServico: (t.tipoServico || '') as PaymentServiceType,
      paidAt: t.data || '',
      status: 'success' as const,
    });

    // Try fast dedicated endpoint first, fallback to transactions
    try {
      const response = await api.get('/client/recent-payments?limit=5');
      if (response.data?.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setRecentPayments(response.data.data.map(mapPayment));
        return;
      }
    } catch { /* try fallback */ }

    try {
      const response = await api.get('/client/transactions?limit=10&sispStatus=N');
      if (response.data?.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setRecentPayments(response.data.data.map(mapPayment));
      }
    } catch { /* keep localStorage data */ }
  };

  useEffect(() => {
    if (selectedCategory) {
      loadServices(selectedCategory);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const data = await servicesService.getCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('payments.errorLoadingCategories'));
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (categoryId: string) => {
    setLoadingServices(true);
    try {
      const data = await servicesService.getServicesByCategory(categoryId);
      setServices(data);
    } catch (err) {
      console.error('Error loading services:', err);
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const loadWalletCards = async () => {
    try {
      const cards: WalletCard[] = [];
      // eCard SISP — cartões tokenizados para pagamento directo
      try {
        const savedCards = await walletService.getCards();
        for (const card of savedCards) {
          cards.push({
            id: card.id,
            token: card.token,
            corTema: '#6233A0',
            descricao: 'Pagali Card',
            DESIG: 'Pagali Card',
            designacao: `${card.cardType || 'Vinti4'} ••••${card.lastFourDigits}`,
            NUMERO: `****${card.lastFourDigits}`,
            pan: `****${card.lastFourDigits}`,
          });
        }
      } catch (_) {}
      setWalletCards(cards);
    } catch (err) {
      console.error('Error loading wallet cards:', err);
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    // Check if category should redirect to its own page
    const category = categories.find((c) => getCategoryId(c) === categoryId);
    const name = (category?.nome || category?.DESIGNACAO || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (name.includes('evento')) {
      navigate('/eventos');
      return;
    }
    if (name.includes('doacao') || name.includes('donativo') || name.includes('donativos')) {
      navigate('/doacoes');
      return;
    }
    // Categoria com rota personalizada (ex: /other-entity → abre modal Outras Entidades)
    if (category?.rota === '/other-entity' || name.includes('outras entidade')) {
      setOtherEntityModalOpen(true);
      return;
    }
    setSelectedCategory(categoryId);
    setSelectedService(null);
  };

  const handleServiceClick = (service: Service) => {
    setSelectedService(service);
    setPrefillData(null);
    setFormModalOpen(true);
  };

  // Handle search result selection
  const handleSearchSelect = (service: Service) => {
    setSelectedService(service);
    setPrefillData(null);
    setFormModalOpen(true);
  };

  // Handle favorite click
  const handleFavoriteClick = (favorite: FavoriteService) => {
    // Create a Service object from the favorite
    const service: Service = {
      id: favorite.serviceId,
      nome: favorite.serviceName,
      logo: favorite.serviceLogo,
      mimeType: favorite.serviceMimeType,
    };
    setSelectedService(service);
    setPrefillData(null);
    setFormModalOpen(true);
  };

  // Handle remove favorite
  const handleRemoveFavorite = (serviceId: string) => {
    paymentService.removeFavorite(serviceId);
    setFavorites(paymentService.getFavorites());
  };

  // Handle add to favorites (from service item)
  const handleAddFavorite = (service: Service, categoryId: string, categoryName: string) => {
    paymentService.addFavorite({
      serviceId: getServiceId(service),
      serviceName: getServiceName(service),
      serviceLogo: getServiceLogo(service),
      serviceMimeType: getServiceMimeType(service),
      categoryId,
      categoryName,
    });
    setFavorites(paymentService.getFavorites());
  };

  // Handle saved reference click
  const handleUseSavedReference = (ref: SavedReference) => {
    const service: Service = {
      id: ref.serviceId,
      nome: ref.serviceName,
      logo: ref.serviceLogo,
      mimeType: ref.serviceMimeType,
      tipoServico: ref.tipoServico,
    };
    setSelectedService(service);
    setPrefillData({
      referencia: ref.referencia,
      entidade: ref.entidade,
    });
    setFormModalOpen(true);
  };

  // Handle remove saved reference
  const handleRemoveSavedReference = async (id: string) => {
    await paymentService.removeSavedReference(id);
    setSavedReferences(paymentService.getSavedReferences());
  };

  // Handle edit saved reference
  const handleEditSavedReference = async (id: string, label: string) => {
    await paymentService.updateSavedReference(id, { label });
    setSavedReferences(paymentService.getSavedReferences());
  };

  // Handle repeat recent payment
  const handleRepeatPayment = (payment: RecentPayment) => {
    const service: Service = {
      id: payment.serviceId,
      nome: payment.serviceName,
      logo: payment.serviceLogo,
      mimeType: payment.serviceMimeType,
      tipoServico: payment.tipoServico,
      entidadeSisp: payment.entidadeSisp,
    } as Service;
    setSelectedService(service);
    setPrefillData({
      // Para recargas/serviços: usar o nº do contador/telemóvel (referenciaServico),
      // não a ref da transação SISP
      referencia: payment.referenciaServico || payment.referencia,
      // Código SISP; sem ele vai o nome e a API converte-o (ou recusa sem cobrar)
      entidade: payment.entidadeSisp || payment.entidade,
      montante: String(payment.montante),
    });
    setFormModalOpen(true);
  };

  // Handle QR code scan
  const handleQRScan = (data: QRPaymentData) => {
    // If we have a serviceId, try to load that service
    // Otherwise, open a generic payment form
    setPrefillData({
      referencia: data.referencia,
      entidade: data.entidade,
      montante: data.montante ? String(data.montante) : undefined,
    });

    // For now, we'll need to select a service manually
    // In a full implementation, we could look up the service by entity code
    setQrScannerOpen(false);
  };

  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      setServices([]);
    }
  };

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [currentTransactionId, setCurrentTransactionId] = useState<string | null>(null);

  const handlePaymentSubmit = useCallback(
    async (data: {
      referencia: string;
      montante: string;
      entidade?: string;
      useWallet: boolean;
      walletCardId?: string;
    }) => {
      if (!selectedService) return;

      const serviceType = (getServiceCode(selectedService) as PaymentServiceType) || 'WBS';
      setPaymentLoading(true);
      setPaymentError(null);

      try {
        // Chamar API para criar pedido SISP real
        const result = await paymentService.initiatePayment({
          serviceId: getServicePaymentPageId(selectedService) || getServiceId(selectedService),
          serviceType,
          amount: parseFloat(data.montante),
          reference: data.referencia,
          entityCode: data.entidade || (selectedService as any)?.entidadeSisp,
          walletCardId: data.useWallet ? data.walletCardId : undefined,
          // Pagali eCard = token directo do utilizador — NÃO usar 'wallet' (system token)
          paymentMethod: undefined,
          descricao: `${getServiceName(selectedService)} - ${(selectedService as any)?.entidade || ''}`.trim(),
        });

        if (!result.success) {
          setPaymentError(result.error || t('payments.initiatePaymentError'));
          setPaymentLoading(false);
          return;
        }

        // Pagamento direto (wallet/token) — sem redirect para SISP
        if (result.paymentMethod === 'wallet' || !result.paymentUrl) {
          setPaymentLoading(false);
          setFormModalOpen(false);
          navigate(`/payments/result?status=success&ref=${result.merchantRef || result.transactionId || ''}`);
          return;
        }

        // Guardar nos recentes
        paymentService.addRecentPayment({
          serviceId: getServiceId(selectedService),
          serviceName: getServiceName(selectedService),
          serviceLogo: getServiceLogo(selectedService),
          serviceMimeType: getServiceMimeType(selectedService),
          referencia: data.referencia,
          entidade: data.entidade,
          montante: parseFloat(data.montante),
          tipoServico: serviceType,
          status: 'pending',
        });
        setRecentPayments(paymentService.getRecentPayments(5));

        // Guardar transaction ID para polling de status
        setCurrentTransactionId(result.transactionId || null);

        // Open SISP payment in popup window
        setFormModalOpen(false);
        sessionStorage.setItem('pendingPaymentRef', result.merchantRef || '');
        const w = 500, h = 650;
        const left = (window.screen.width - w) / 2, top = (window.screen.height - h) / 2;
        const popup = window.open(result.paymentUrl, 'PagamentoSISP', `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`);
        if (!popup) { window.location.href = result.paymentUrl; return; }
        const pollRef = result.merchantRef;

        // 1. Listener para postMessage enviado pelo callback SISP (mais rápido e fiável)
        let poll: ReturnType<typeof setInterval>;
        const msgHandler = (event: MessageEvent) => {
          const data = event.data;
          if (!data || data.type !== 'sisp-result') return;
          window.removeEventListener('message', msgHandler);
          clearInterval(poll);
          if (data.status === 'success') {
            navigate('/payments/result?status=success&ref=' + (data.ref || pollRef));
          } else {
            const errParam = data.error ? '&error=' + encodeURIComponent(data.error) : '';
            navigate('/payments/result?status=failed&ref=' + (data.ref || pollRef) + errParam);
          }
        };
        window.addEventListener('message', msgHandler);

        // 2. Polling: fallback para quando postMessage não chegar (popup fechou sem mensagem)
        poll = setInterval(async () => {
          if (popup.closed) {
            clearInterval(poll);
            window.removeEventListener('message', msgHandler);
            if (pollRef) {
              // Aguardar 2s para o callback SISP processar antes de consultar
              await new Promise(r => setTimeout(r, 2000));
              const st = await paymentService.getPaymentStatusByRef(pollRef);
              if (st?.status === 'completed') {
                navigate('/payments/result?status=success&ref=' + pollRef);
              } else if (st?.status === 'failed') {
                navigate('/payments/result?status=failed&ref=' + pollRef);
              } else {
                // Ainda pendente — tentar mais uma vez após 3s
                await new Promise(r => setTimeout(r, 3000));
                const st2 = await paymentService.getPaymentStatusByRef(pollRef);
                if (st2?.status === 'completed') {
                  navigate('/payments/result?status=success&ref=' + pollRef);
                } else {
                  navigate('/payments/result?status=failed&ref=' + pollRef);
                }
              }
            }
          }
        }, 1000);
      } catch (err) {
        setPaymentError(err instanceof Error ? err.message : t('payments.initiatePaymentError'));
      } finally {
        setPaymentLoading(false);
      }
    },
    [selectedService]
  );

  const handlePaymentComplete = useCallback((status: 'completed' | 'failed') => {
    setPaymentModalOpen(false);
    setPaymentUrl('');
    setSelectedService(null);
    setCurrentTransactionId(null);

    // Navegar para a página de resultado
    navigate(`/payments/result?status=${status === 'completed' ? 'success' : 'failed'}`);
  }, [navigate]);

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentUrl('');
    setSelectedService(null);
    setCurrentTransactionId(null);
    if (selectedCategory) {
      loadServices(selectedCategory);
    }
  };

  // Get current category name
  const currentCategoryName =
    categoriaName ||
    categories.find((c) => getCategoryId(c) === selectedCategory)?.nome ||
    categories.find((c) => getCategoryId(c) === selectedCategory)?.DESIGNACAO ||
    t('nav.services');

  if (loading) {
    return <LoaderComponent size="lg" text={t('payments.loadingServices')} />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-pagali-error mb-4">{error}</p>
        <button onClick={() => window.location.reload()} className="btn-primary">
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedCategory && (
            <button
              onClick={handleBack}
              className="p-2 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-600 transition-colors"
            >
              <MdArrowBack className="text-xl" />
            </button>
          )}
          <div>
            <h2 className="text-2xl font-semibold text-pagali-gray-800">
              {selectedCategory ? currentCategoryName : t('payments.servicePayment')}
            </h2>
            <p className="text-pagali-gray-500">
              {selectedCategory
                ? t('payments.selectService')
                : t('payments.chooseCategory')}
            </p>
          </div>
        </div>

        {showCategoriesView && (
          <button
            onClick={() => setOtherEntityModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-pagali-primary text-pagali-primary rounded-xl hover:bg-pagali-primary/5 transition-colors"
            title="Pagar fatura de entidade não listada (ex: Electra Norte 35, Electra Sul 36)"
          >
            <MdPayments />
            <span className="hidden sm:inline">Outras Entidades</span>
          </button>
        )}
      </div>

      {/* Search Bar - Only on categories view */}
      {showCategoriesView && (
        <PaymentSearchBar onServiceSelect={handleSearchSelect} />
      )}

      {/* Prefill Data Notice */}
      {prefillData && prefillData.referencia && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-800 font-medium">{t('payments.qrDataReady')}</p>
            <p className="text-xs text-blue-600">
              Ref: {prefillData.referencia}
              {prefillData.entidade && ` | Ent: ${prefillData.entidade}`}
              {prefillData.montante && ` | ${prefillData.montante}$`}
            </p>
          </div>
          <button
            onClick={() => setPrefillData(null)}
            className="text-blue-600 hover:text-blue-800 p-1"
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Main Layout: Categories + Recent Payments side by side */}
      {showCategoriesView && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Column 1: Categories */}
          <div className="space-y-4">
            <Card>
              <h3 className="font-semibold text-pagali-gray-800 mb-4">{t('payments.serviceCategories')}</h3>
              {categories.length === 0 ? (
                <EmptyState
                  icon={<MdPayments className="text-5xl" />}
                  title={t('payments.noCategories')}
                  description={t('payments.noCategoriesDesc')}
                />
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {categories.map((category, index) => (
                    <CategoryItem
                      key={getCategoryId(category)}
                      category={category}
                      index={index}
                      onClick={() => handleCategoryClick(getCategoryId(category))}
                    />
                  ))}
                </div>
              )}
            </Card>

            {/* Favorites */}
            <FavoriteServices
              favorites={favorites}
              onServiceClick={handleFavoriteClick}
              onRemove={handleRemoveFavorite}
            />

            {/* Saved References */}
            <SavedReferencesSection
              references={savedReferences}
              onUseReference={handleUseSavedReference}
              onRemove={handleRemoveSavedReference}
              onEdit={handleEditSavedReference}
            />
          </div>

          {/* Column 2: Recent Payments + Bill Estimate */}
          <div className="space-y-4">
            <RecentPaymentsSection
              payments={recentPayments}
              onRepeat={handleRepeatPayment}
              onViewReceipt={(payment) => navigate('/transactions', { state: { openTransaction: payment.id } })}
            />

            <BillEstimateCard />
          </div>
        </div>

        {/* Advertising Cards */}
        <AdCards />
        </>
      )}

      {/* Services List */}
      {showServicesView && (
        <div>
          {loadingServices ? (
            <LoaderComponent size="md" text={t('payments.loadingServices')} />
          ) : services.length === 0 ? (
            <Card>
              <EmptyState
                icon={<MdPayments className="text-5xl" />}
                title={t('payments.noServices')}
                description={t('payments.noServicesDesc')}
              />
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {services.map((service) => (
                  <ServiceItem
                    key={getServiceId(service)}
                    service={service}
                    onClick={() => handleServiceClick(service)}
                  />
                ))}
              </div>
              {/* Área de publicidade desta página (Admin › Publicidade › App) */}
              <AdCards spot={`CAT${selectedCategory}`} limit={4} banner />
            </>
          )}
        </div>
      )}

      {/* Payment Form Modal */}
      <PaymentFormModal
        isOpen={formModalOpen}
        service={selectedService}
        walletCards={walletCards}
        prefillData={prefillData}
        isFavorite={selectedService ? paymentService.isFavorite(getServiceId(selectedService)) : false}
        categoryId={selectedCategory || undefined}
        categoryName={currentCategoryName}
        loading={paymentLoading}
        onSubmit={handlePaymentSubmit}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedService(null);
          setPrefillData(null);
        }}
        onToggleFavorite={selectedService ? () => {
          const serviceId = getServiceId(selectedService);
          if (paymentService.isFavorite(serviceId)) {
            paymentService.removeFavorite(serviceId);
          } else {
            paymentService.addFavorite({
              serviceId,
              serviceName: getServiceName(selectedService),
              serviceLogo: getServiceLogo(selectedService),
              serviceMimeType: getServiceMimeType(selectedService),
              categoryId: selectedCategory || '',
              categoryName: currentCategoryName,
            });
          }
          setFavorites(paymentService.getFavorites());
        } : undefined}
        onSaveReference={selectedService ? (label, referencia, entidade) => {
          const serviceType = (getServiceCode(selectedService) as PaymentServiceType) || 'WBS';
          paymentService.addSavedReference({
            serviceId: getServiceId(selectedService),
            serviceName: getServiceName(selectedService),
            serviceLogo: getServiceLogo(selectedService),
            serviceMimeType: getServiceMimeType(selectedService),
            label,
            referencia,
            entidade,
            tipoServico: serviceType,
          }).then(() => {
            setSavedReferences(paymentService.getSavedReferences());
          });
        } : undefined}
      />

      {/* Payment Error */}
      {paymentError && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-red-50 border border-red-200 rounded-xl p-4 shadow-lg max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <FiAlertCircle className="text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">{t('payments.paymentError')}</p>
              <p className="text-xs text-red-600">{paymentError}</p>
            </div>
            <button onClick={() => setPaymentError(null)} className="text-red-400 hover:text-red-600">
              <FiX />
            </button>
          </div>
        </div>
      )}

      {/* Payment Iframe Modal */}
      <PaymentIframeModal
        isOpen={paymentModalOpen}
        paymentUrl={paymentUrl}
        transactionId={currentTransactionId}
        onClose={handleClosePaymentModal}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* QR Code Scanner Modal */}
      <QRCodeScanner
        isOpen={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onScan={handleQRScan}
      />

      {/* Other Entity Modal — pagamento manual de fatura por código SISP (BT202411A) */}
      <OtherEntityModal
        isOpen={otherEntityModalOpen}
        loading={otherEntityLoading}
        onClose={() => setOtherEntityModalOpen(false)}
        onSubmit={async ({ entityCode, referencia, montante }) => {
          setOtherEntityLoading(true);
          setPaymentError(null);
          try {
            const result = await paymentService.initiatePayment({
              serviceId: '',
              serviceType: 'WBS' as PaymentServiceType,
              amount: parseFloat(montante),
              reference: referencia,
              entityCode,
              transactionCode: '2',
              descricao: `Pagamento entidade ${entityCode}`,
            } as any);

            if (!result.success || !result.paymentUrl) {
              setPaymentError(result.error || 'Erro ao iniciar pagamento');
              setOtherEntityLoading(false);
              return;
            }

            setOtherEntityModalOpen(false);
            sessionStorage.setItem('pendingPaymentRef', result.merchantRef || '');
            const w = 500, h = 650;
            const left = (window.screen.width - w) / 2, top = (window.screen.height - h) / 2;
            const popup = window.open(result.paymentUrl, 'PagamentoSISP', `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`);
            if (!popup) { window.location.href = result.paymentUrl; return; }
            const pollRef = result.merchantRef;
            let pollOther: ReturnType<typeof setInterval>;
            const msgHandlerOther = (event: MessageEvent) => {
              const data = event.data;
              if (!data || data.type !== 'sisp-result') return;
              window.removeEventListener('message', msgHandlerOther);
              clearInterval(pollOther);
              if (data.status === 'success') {
                navigate('/payments/result?status=success&ref=' + (data.ref || pollRef));
              } else {
                const errParam = data.error ? '&error=' + encodeURIComponent(data.error) : '';
                navigate('/payments/result?status=failed&ref=' + (data.ref || pollRef) + errParam);
              }
            };
            window.addEventListener('message', msgHandlerOther);
            pollOther = setInterval(async () => {
              if (popup.closed) {
                clearInterval(pollOther);
                window.removeEventListener('message', msgHandlerOther);
                if (pollRef) {
                  await new Promise(r => setTimeout(r, 2000));
                  const st = await paymentService.getPaymentStatusByRef(pollRef);
                  if (st?.status === 'completed') navigate('/payments/result?status=success&ref=' + pollRef);
                  else if (st?.status === 'failed') navigate('/payments/result?status=failed&ref=' + pollRef);
                  else {
                    await new Promise(r => setTimeout(r, 3000));
                    const st2 = await paymentService.getPaymentStatusByRef(pollRef);
                    if (st2?.status === 'completed') navigate('/payments/result?status=success&ref=' + pollRef);
                    else navigate('/payments/result?status=failed&ref=' + pollRef);
                  }
                }
              }
            }, 1000);
          } catch (err) {
            setPaymentError(err instanceof Error ? err.message : 'Erro ao iniciar pagamento');
          } finally {
            setOtherEntityLoading(false);
          }
        }}
      />
    </div>
  );
};

export default Payments;
